import React, { useState, useEffect, useRef, useCallback } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import {
  Box,
  useTheme,
  Alert,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Tooltip,
} from "@mui/material";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogisticsFilters from "./components/LogisticsFilters";
import LogisticsList from "./components/LogisticsList";
import LogisticsMapPanel from "./components/LogisticsMapPanel";
import { LogisticInvoice } from "../QueueBoard/models/logisticsTypes";
import { InvoiceAPI } from "../QueueBoard/api/logisticsApi";
import { useWorkOrderStore } from "../store/zustandWorkOrderStore";
import localStorageHelper from "app/localStorage";
import { useLookupData } from "components/common/hooks/useLookupData";
import { User } from "components/authentication/models/authTypes";
import { LOGISTICS_SEARCH_INFO_ALERT } from "components/common/constants/filterParamConstants";
import GlobalSpinner from "components/common/components/Spinner/GlobalSpinner";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { validateAddress } from "./utils/addressUtility";
import packageJson from "../../../package.json";

const containerStyle = {
  width: "100%",
  height: "100%",
};

const defaultCenter = { lat: 43.6532, lng: -79.3832 }; // Toronto

interface LogisticsContainerProps {}

const LogisticsContainer: React.FC<LogisticsContainerProps> = () => {
  const theme = useTheme();
  // Ensure lookups (including work order statuses) are loaded into the store
  useLookupData();
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDipPUXM5YzthCmrSesDjYPvc6-LmfBByk",
    libraries: ["places"],
  });

  const mapRef = useRef(null);
  const printRef = useRef(null);
  const hasGeocodedInitialStartingPoint = useRef(false);
  const isInitializingStartingPoint = useRef(false); // Track if we're in initial validation phase
  const lastGeocodedStartingPointRef = useRef<string>(""); // Track the last startingPoint value we geocoded
  const { woStatuses, locations } = useWorkOrderStore();

  // Data state
  const [workOrders, setWorkOrders] = useState<LogisticInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // Print dialog state
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printRouteType, setPrintRouteType] = useState<"user" | "optimized">(
    "user",
  );

  // Panel collapse state
  const [isListCollapsed, setIsListCollapsed] = useState(false);

  // Selection state
  const [selectedWorkOrders, setSelectedWorkOrders] = useState<number[]>([]);
  const [selectedWorkOrdersOrder, setSelectedWorkOrdersOrder] = useState<
    number[]
  >([]);
  // Store full work order data for selected items (persists across searches)
  const [selectedWorkOrdersData, setSelectedWorkOrdersData] = useState<
    Map<number, LogisticInvoice>
  >(new Map());

  // Map state
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [optimizedDirectionsResponse, setOptimizedDirectionsResponse] =
    useState<google.maps.DirectionsResult | null>(null);
  const [optimizedRouteOrder, setOptimizedRouteOrder] = useState<number[]>([]); // Stores optimized work order IDs in order
  const [showOptimizedRoute, setShowOptimizedRoute] = useState<boolean>(true);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [placeDetails, setPlaceDetails] = useState(null);
  const [addresses, setAddresses] = useState<Map<number, string>>(new Map());
  const [geocodedAddresses, setGeocodedAddresses] = useState<
    Map<number, { lat: number; lng: number }>
  >(new Map());
  const geocodedAddressesRef = useRef<
    Map<number, { lat: number; lng: number }>
  >(new Map());
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(
    null,
  );
  const lastRouteSignatureRef = useRef<string | null>(null);

  // User and map center
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user"),
  );
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  useEffect(() => {
    if (!user) return;
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "googleMapsLogisticsContainerLoad",
      {
        elapsed_ms: "0",
        title: "Logistics",
        componentName: "LogisticsContainer.tsx",
        version: packageJson.version,
      },
    );
  }, [user]);

  // Filter state
  const [dateRange, setDateRange] = useState<[Date | null, Date | null] | null>(
    null,
  );
  const [searchText, setSearchText] = useState<string>("");
  const [WorkOrderStatusIds, setWorkOrderStatusIds] = useState<string[]>([]);
  const [pickupDateRange, setPickupDateRange] = useState<
    [Date | null, Date | null] | null
  >(null);
  const [deliveryDateRange, setDeliveryDateRange] = useState<
    [Date | null, Date | null] | null
  >(null);
  const [locationId, setLocationId] = useState<string>(() => {
    const userName = localStorage.getItem("username") || "";
    const storedLocation = localStorage.getItem(`SELECTED_WO_LOCS_${userName}`);
    return storedLocation ? storedLocation.split(",")[0] : "0"; // Get first location if multiple, default to "0"
  });
  const [startingPoint, setStartingPoint] = useState<string>("");
  const [hasUserProvidedStartingPoint, setHasUserProvidedStartingPoint] =
    useState<boolean>(false);
  const [isUserTypingStartingPoint, setIsUserTypingStartingPoint] =
    useState<boolean>(false);
  const [startingPointCoordinates, setStartingPointCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [includeRentalContracts, setIncludeRentalContracts] =
    useState<boolean>(false);
  const [invalidWorkOrderAddressIds, setInvalidWorkOrderAddressIds] = useState<
    Set<number>
  >(new Set());

  // Geocode user's location address to set map center
  useEffect(() => {
    const geocodeUserLocation = async () => {
      if (user?.locationAddress && isLoaded) {
        try {
          const coordinates = await geocodeAddress(user.locationAddress);
          if (coordinates) {
            setMapCenter(coordinates);
            //console.log("Map center set to user location:", coordinates);
          } else {
            //console.log("Failed to geocode user location, using default");
            setMapCenter(defaultCenter);
          }
        } catch (error) {
          //console.error("Error geocoding user location:", error);
          setMapCenter(defaultCenter);
        }
      } else {
        setMapCenter(defaultCenter);
      }
    };

    geocodeUserLocation();
  }, [user?.locationAddress, isLoaded]);

  // Helper function to validate and set startingPoint
  // If address is valid, sets it; if invalid, sets empty string
  const validateAndSetStartingPoint = useCallback(
    async (address: string) => {
      // console.log(
      //   "[validateAndSetStartingPoint] Called with address:",
      //   address,
      // );
      if (!address || !address.trim()) {
        //console.log("[validateAndSetStartingPoint] Empty address, clearing");
        isInitializingStartingPoint.current = false;
        setStartingPoint("");
        return;
      }

      if (!isLoaded) {
        // console.log(
        //   "[validateAndSetStartingPoint] Google Maps not loaded, clearing",
        // );
        isInitializingStartingPoint.current = false;
        setStartingPoint("");
        return;
      }

      // Mark that we're initializing to prevent handleStartingPointChange from interfering
      isInitializingStartingPoint.current = true;
      // console.log(
      //   "[validateAndSetStartingPoint] Starting validation for:",
      //   address.trim(),
      // );
      try {
        const coordinates = await validateAddress(address.trim());
        if (coordinates) {
          // Address is valid, set it
          // console.log(
          //   "[validateAndSetStartingPoint] Validation SUCCESS, setting startingPoint to:",
          //   address.trim(),
          // );
          setStartingPoint(address.trim());
          // Allow a small delay before allowing handleStartingPointChange to run
          // This ensures the value is set before handleStartingPointChange processes it
          setTimeout(() => {
            isInitializingStartingPoint.current = false;
            // console.log(
            //   "[validateAndSetStartingPoint] Initialization flag cleared",
            // );
          }, 100);
        } else {
          // Address is invalid, set empty string
          // console.log(
          //   "[validateAndSetStartingPoint] Validation FAILED (no coordinates), clearing",
          // );
          isInitializingStartingPoint.current = false;
          setStartingPoint("");
        }
      } catch (error) {
        // console.error(
        //   "[validateAndSetStartingPoint] Error validating address:",
        //   error,
        // );
        // On error, set empty string
        isInitializingStartingPoint.current = false;
        setStartingPoint("");
      }
    },
    [isLoaded],
  );

  // Validate and set initial user location address on page load
  useEffect(() => {
    const validateInitialUserLocation = async () => {
      // Only validate if:
      // 1. Google Maps is loaded
      // 2. User has a location address
      // 3. Starting point is still empty (hasn't been set yet)
      if (isLoaded && user?.locationAddress && !startingPoint) {
        await validateAndSetStartingPoint(user.locationAddress);
      }
    };

    validateInitialUserLocation();
  }, [
    isLoaded,
    user?.locationAddress,
    startingPoint,
    validateAndSetStartingPoint,
  ]);

  // Geocode initial starting point on page load if it has a valid address
  // This enables the checkbox if startingPoint has a valid address initially
  useEffect(() => {
    const geocodeInitialStartingPoint = async () => {
      // console.log(
      //   "[geocodeInitialStartingPoint] Effect triggered, startingPoint:",
      //   startingPoint,
      //   "hasGeocoded:",
      //   hasGeocodedInitialStartingPoint.current,
      //   "hasCoordinates:",
      //   !!startingPointCoordinates,
      // );

      // Only geocode if:
      // 1. Google Maps is loaded
      // 2. startingPoint has a value (not empty)
      // 3. We haven't already geocoded this exact starting point value
      // 4. We don't already have coordinates (to avoid re-geocoding if user selected from autocomplete)
      if (
        isLoaded &&
        startingPoint &&
        startingPoint.trim() &&
        startingPoint.trim() !== lastGeocodedStartingPointRef.current &&
        !startingPointCoordinates
      ) {
        const addressToGeocode = startingPoint.trim();
        // console.log(
        //   "[geocodeInitialStartingPoint] Geocoding address:",
        //   addressToGeocode,
        // );

        try {
          // Use validateAddress instead of geocodeAddress because it normalizes the address format
          const coordinates = await validateAddress(addressToGeocode);
          if (coordinates) {
            // console.log(
            //   "[geocodeInitialStartingPoint] Geocoding SUCCESS, setting coordinates:",
            //   coordinates,
            // );
            setStartingPointCoordinates({
              lat: coordinates.lat,
              lng: coordinates.lng,
            });
            setHasUserProvidedStartingPoint(true);
            lastGeocodedStartingPointRef.current = addressToGeocode;
            hasGeocodedInitialStartingPoint.current = true;
          } else {
            // console.log(
            //   "[geocodeInitialStartingPoint] Geocoding FAILED (no coordinates)",
            // );
            setStartingPointCoordinates(null);
            setHasUserProvidedStartingPoint(false);
            hasGeocodedInitialStartingPoint.current = true; // Mark as attempted even if failed
          }
        } catch (error) {
          // console.error(
          //   "[geocodeInitialStartingPoint] Error geocoding initial starting point:",
          //   error,
          // );
          setStartingPointCoordinates(null);
          setHasUserProvidedStartingPoint(false);
          hasGeocodedInitialStartingPoint.current = true; // Mark as attempted even if failed
        }
      } else {
        // console.log(
        //   "[geocodeInitialStartingPoint] Skipping - conditions not met:",
        //   {
        //     isLoaded,
        //     hasStartingPoint: !!(startingPoint && startingPoint.trim()),
        //     isNewValue:
        //       startingPoint?.trim() !== lastGeocodedStartingPointRef.current,
        //     hasCoordinates: !!startingPointCoordinates,
        //   },
        // );
      }
    };

    geocodeInitialStartingPoint();
  }, [isLoaded, startingPoint, startingPointCoordinates]);

  // Intentionally rely on the filters effect below for initial load

  // Date formatting function (same as FilterPanel)
  const getFormattedDate = (inputDate: Date) => {
    return `${inputDate.getFullYear()}-${String(
      inputDate.getMonth() + 1,
    ).padStart(2, "0")}-${String(inputDate.getDate()).padStart(2, "0")}`;
  };

  // Clean address string - remove line breaks and normalize whitespace
  const cleanAddress = (address: string | null | undefined): string => {
    if (!address) return "";
    // Replace \r, \n, \r\n with space, then normalize multiple spaces to single space, trim
    return address
      .replace(/\r\n/g, " ")
      .replace(/\r/g, " ")
      .replace(/\n/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const loadWorkOrders = async () => {
    setLoading(true);
    try {
      // Only call API if we have a complete date range (both start and end dates)
      let fromDate: string | undefined = undefined;
      let toDate: string | undefined = undefined;

      if (dateRange && dateRange[0] && dateRange[1]) {
        fromDate = getFormattedDate(dateRange[0]);
        toDate = getFormattedDate(dateRange[1]);
      }

      // Format pickup and delivery date ranges
      const pickupStartDate =
        pickupDateRange && pickupDateRange[0]
          ? getFormattedDate(pickupDateRange[0])
          : undefined;
      const pickupEndDate =
        pickupDateRange && pickupDateRange[1]
          ? getFormattedDate(pickupDateRange[1])
          : undefined;
      const deliveryStartDate =
        deliveryDateRange && deliveryDateRange[0]
          ? getFormattedDate(deliveryDateRange[0])
          : undefined;
      const deliveryEndDate =
        deliveryDateRange && deliveryDateRange[1]
          ? getFormattedDate(deliveryDateRange[1])
          : undefined;

      const data = await InvoiceAPI.getFilteredInvoices(
        fromDate,
        toDate,
        searchText || undefined,
        includeRentalContracts,
        WorkOrderStatusIds.length > 0 ? WorkOrderStatusIds : undefined,
        locationId !== "0" ? locationId : undefined,
        pickupStartDate,
        pickupEndDate,
        deliveryStartDate,
        deliveryEndDate,
      );
      const list = Array.isArray(data) ? data : [];
      setWorkOrders(list);
      // Preserve user-edited addresses by merging with existing addresses
      // Only set address from API if it doesn't already exist in the map
      setAddresses((prevAddresses) => {
        const newAddresses = new Map(prevAddresses);
        list.forEach((wo) => {
          // Only set if not already in the map (preserve user edits)
          if (!newAddresses.has(wo.invoiceDocumentIdentity)) {
            newAddresses.set(
              wo.invoiceDocumentIdentity,
              cleanAddress(wo.customerAddress),
            );
          }
        });
        return newAddresses;
      });
    } catch (error) {
      console.error("Error loading work orders:", error);
      setWorkOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle starting point changes and update map center
  // NOTE: This should NOT enable the checkbox - only handleStartingPointSelect should do that
  const handleStartingPointChange = useCallback(
    async (address: string) => {
      // Skip if we're in the middle of initializing/validating the starting point
      // This prevents race conditions during page load
      if (isInitializingStartingPoint.current) {
        return;
      }

      if (!address.trim() || !isLoaded) {
        // If no address, reset to user location or default
        // Don't change hasUserProvidedStartingPoint here - only handleStartingPointSelect controls it
        if (user?.locationAddress) {
          try {
            const coordinates = await geocodeAddress(user.locationAddress);
            if (coordinates) {
              setMapCenter(coordinates);
            } else {
              setMapCenter(defaultCenter);
            }
          } catch (error) {
            console.error("Error geocoding user location:", error);
            setMapCenter(defaultCenter);
          }
        } else {
          setMapCenter(defaultCenter);
        }
        // Only clear coordinates if address is empty AND we're not initializing
        // This prevents clearing coordinates during initial validation
        if (!address.trim() && !isInitializingStartingPoint.current) {
          setStartingPointCoordinates(null);
        }
        return;
      }

      // Try to geocode for map center update only, but DON'T set coordinates
      // Coordinates should ONLY be set when user selects from autocomplete dropdown (handleStartingPointSelect)
      // This ensures checkbox only shows when user explicitly selects a valid address
      try {
        const coordinates = await geocodeAddress(address);
        if (coordinates) {
          // Only update map center for visualization, but don't set startingPointCoordinates
          // startingPointCoordinates should only be set in handleStartingPointSelect
          setMapCenter(coordinates);
          //console.log("Map center updated to starting point:", coordinates);
        } else {
          //console.log("Failed to geocode starting point address:", address);
          // Don't clear coordinates - they might be valid from a previous selection
        }
      } catch (error) {
        //console.error("Error geocoding starting point:", error);
        // Don't clear coordinates on error - they might be valid from a selection
      }
    },
    [isLoaded, user?.locationAddress],
  );

  // When starting point changes, update map center
  useEffect(() => {
    handleStartingPointChange(startingPoint);
  }, [startingPoint, handleStartingPointChange]);

  const resetFilters = () => {
    const userName = localStorage.getItem("username") || "";
    setDateRange(null);
    setSearchText("");
    setWorkOrderStatusIds([]);
    setPickupDateRange(null);
    setDeliveryDateRange(null);
    setLocationId("0");
    localStorage.setItem(`SELECTED_WO_LOCS_${userName}`, "0");
    // Validate user location address before setting
    if (user?.locationAddress) {
      validateAndSetStartingPoint(user.locationAddress);
    } else {
      isInitializingStartingPoint.current = false;
      setStartingPoint("");
    }
    setIncludeRentalContracts(false);
    setStartingPointCoordinates(null);
    setHasUserProvidedStartingPoint(false);
    setIsUserTypingStartingPoint(false);
    hasGeocodedInitialStartingPoint.current = false; // Reset ref so initial value can be geocoded again
    lastGeocodedStartingPointRef.current = ""; // Reset last geocoded value
    // Clear all selected invoices (uncheck all checkboxes)
    setSelectedWorkOrders([]);
    setSelectedWorkOrdersOrder([]);
    setSelectedWorkOrdersData(new Map());
    // Clear addresses and geocoded addresses
    setAddresses(new Map());
    setGeocodedAddresses(new Map());
    geocodedAddressesRef.current = new Map();
    // Clear directions
    setDirectionsResponse(null);
    setOptimizedDirectionsResponse(null);
    setOptimizedRouteOrder([]);
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setDirections(null);
    }
    // Clear invalid address IDs
    setInvalidWorkOrderAddressIds(new Set());
  };

  // Function to fully reset the map (used by Reset button only)
  const resetMap = useCallback(() => {
    // Clear selected work orders
    setSelectedWorkOrders([]);
    setSelectedWorkOrdersOrder([]);
    setSelectedWorkOrdersData(new Map());

    // Clear addresses and geocoded addresses
    setAddresses(new Map());
    setGeocodedAddresses(new Map());
    geocodedAddressesRef.current = new Map();

    // Clear directions
    setDirectionsResponse(null);
    setOptimizedDirectionsResponse(null);
    setOptimizedRouteOrder([]);
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setDirections(null);
    }

    // Reset map center to default or user location
    if (user?.locationAddress) {
      handleStartingPointChange(user.locationAddress);
    } else {
      setMapCenter(defaultCenter);
    }

    // Clear invalid address IDs
    setInvalidWorkOrderAddressIds(new Set());

    // console.log("Map reset completed");
  }, [user?.locationAddress, handleStartingPointChange]);

  // Function to refresh map data without clearing selections (used when filters change)
  const refreshMapData = useCallback(() => {
    // Don't clear anything - we want to preserve:
    // - selectedWorkOrders and selectedWorkOrdersOrder (user's selection)
    // - addresses and geocodedAddresses (cached address data)
    // - directionsResponse (calculated route - still valid for the selected work orders)
    // This allows users to keep their selection when searching for additional work orders
  }, []);

  // When filters change, fetch from backend but preserve selections
  useEffect(() => {
    loadWorkOrders();
    refreshMapData(); // Refresh map data but keep selections
  }, [
    dateRange,
    searchText,
    WorkOrderStatusIds,
    pickupDateRange,
    deliveryDateRange,
    locationId,
    includeRentalContracts,
    refreshMapData,
  ]);

  const handleCheckboxChange = async (workOrderId: number) => {
    const isCurrentlySelected = selectedWorkOrders.includes(workOrderId);
    const workOrder = workOrders.find(
      (wo) => wo.invoiceDocumentIdentity === workOrderId,
    );

    if (user) {
      const action = isCurrentlySelected ? "Uncheck" : "Check";
      const invoiceInfo = workOrder?.invoiceNumber
        ? ` - Invoice #${workOrder.invoiceNumber}`
        : "";
      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        "googleMapWorkOrderToggle",
        {
          elapsed_ms: "0",
          title: `${action} Work Order${invoiceInfo}`,
          componentName: "LogisticsContainer.tsx",
          version: packageJson.version,
        },
      );
    }

    // If unchecking, proceed normally
    if (isCurrentlySelected) {
      setSelectedWorkOrders((prev) => prev.filter((id) => id !== workOrderId));
      setSelectedWorkOrdersOrder((prev) =>
        prev.filter((id) => id !== workOrderId),
      );
      // Remove from selectedWorkOrdersData
      setSelectedWorkOrdersData((prev) => {
        const next = new Map(prev);
        next.delete(workOrderId);
        return next;
      });
      // Remove from invalid addresses if it was invalid
      setInvalidWorkOrderAddressIds((prev) => {
        if (!prev.has(workOrderId)) return prev;
        const next = new Set(prev);
        next.delete(workOrderId);
        return next;
      });
      return;
    }

    // If checking, validate the address first
    if (!workOrder) {
      return;
    }

    // Get the current address (from addresses map or default from workOrder)
    const currentAddress =
      addresses.get(workOrderId) ||
      cleanAddress(workOrder.customerAddress) ||
      "";

    if (!currentAddress || !currentAddress.trim()) {
      // No address available, mark as invalid
      setInvalidWorkOrderAddressIds((prev) => {
        if (prev.has(workOrderId)) return prev;
        const next = new Set(prev);
        next.add(workOrderId);
        return next;
      });
      return;
    }

    // Validate the address using Geocoding API
    if (!isLoaded) {
      return;
    }

    try {
      const coordinates = await validateAddress(currentAddress);
      if (coordinates) {
        // Address is valid - proceed with checking
        // Remove from invalid addresses if it was previously invalid
        setInvalidWorkOrderAddressIds((prev) => {
          if (!prev.has(workOrderId)) return prev;
          const next = new Set(prev);
          next.delete(workOrderId);
          return next;
        });

        // Add to selected work orders
        setSelectedWorkOrders((prev) => {
          if (prev.includes(workOrderId)) return prev;
          return [...prev, workOrderId];
        });

        setSelectedWorkOrdersOrder((prev) => {
          if (prev.includes(workOrderId)) return prev;
          return [...prev, workOrderId];
        });

        // Store the full work order data for route calculation (persists across searches)
        setSelectedWorkOrdersData((prev) => {
          const next = new Map(prev);
          next.set(workOrderId, workOrder);
          return next;
        });

        // Store the geocoded coordinates
        setGeocodedAddresses((prev) => {
          const next = new Map(prev);
          next.set(workOrderId, coordinates);
          geocodedAddressesRef.current.set(workOrderId, coordinates);
          return next;
        });
      } else {
        // Address is invalid - don't check it and mark as invalid
        setInvalidWorkOrderAddressIds((prev) => {
          if (prev.has(workOrderId)) return prev;
          const next = new Set(prev);
          next.add(workOrderId);
          return next;
        });
        // Ensure it's not in selected work orders
        setSelectedWorkOrders((prev) =>
          prev.filter((id) => id !== workOrderId),
        );
        setSelectedWorkOrdersOrder((prev) =>
          prev.filter((id) => id !== workOrderId),
        );
      }
    } catch (error) {
      console.error("Error validating address:", error);
      // On error, mark as invalid and don't check
      setInvalidWorkOrderAddressIds((prev) => {
        if (prev.has(workOrderId)) return prev;
        const next = new Set(prev);
        next.add(workOrderId);
        return next;
      });
      setSelectedWorkOrders((prev) => prev.filter((id) => id !== workOrderId));
      setSelectedWorkOrdersOrder((prev) =>
        prev.filter((id) => id !== workOrderId),
      );
    }
  };

  const geocodeAddress = async (
    address: string,
  ): Promise<{ lat: number; lng: number } | null> => {
    if (!window.google) return null;

    const geocoder = new google.maps.Geocoder();
    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
          });
        } else {
          resolve(null);
        }
      });
    });
  };

  const geocodeSelectedAddresses = useCallback(async () => {
    // console.log("geocodeSelectedAddresses called", {
    //   isLoaded,
    //   selectedWorkOrdersLength: selectedWorkOrders.length,
    // });
    if (!isLoaded || selectedWorkOrders.length === 0) {
      // console.log(
      //   "Early return: isLoaded =",
      //   isLoaded,
      //   "selectedWorkOrders.length =",
      //   selectedWorkOrders.length
      // );
      return;
    }

    const newGeocodedAddresses = new Map(geocodedAddressesRef.current);
    let hasNewAddresses = false;

    //console.log("Processing selected work orders:", selectedWorkOrders);

    // Process each selected work order
    for (const workOrderId of selectedWorkOrders) {
      if (!newGeocodedAddresses.has(workOrderId)) {
        const workOrder = workOrders.find(
          (wo) => wo.invoiceDocumentIdentity === workOrderId,
        );
        if (workOrder) {
          const effectiveAddress =
            addresses.get(workOrderId) ||
            cleanAddress(workOrder.customerAddress);
          // console.log(
          //   `Geocoding address for ${workOrder.documentNumber}: ${effectiveAddress}`
          // );
          const coordinates = await geocodeAddress(effectiveAddress || "");
          if (coordinates) {
            //console.log(`Geocoded coordinates:`, coordinates);
            newGeocodedAddresses.set(workOrderId, coordinates);
            hasNewAddresses = true;
            // Remove from invalid addresses if it was previously invalid
            setInvalidWorkOrderAddressIds((prev) => {
              if (!prev.has(workOrderId)) return prev;
              const next = new Set(prev);
              next.delete(workOrderId);
              return next;
            });
          } else {
            // Mark as invalid if geocoding fails
            setInvalidWorkOrderAddressIds((prev) => {
              if (prev.has(workOrderId)) return prev;
              const next = new Set(prev);
              next.add(workOrderId);
              return next;
            });
            // Remove from selected work orders if address is invalid
            setSelectedWorkOrders((prev) =>
              prev.filter((id) => id !== workOrderId),
            );
            setSelectedWorkOrdersOrder((prev) =>
              prev.filter((id) => id !== workOrderId),
            );
            // console.log(
            //   `Failed to geocode address: ${workOrder.customerAddress}`
            // );
          }
        } else {
          // console.log(
          //   `Work order not found or no address for ID: ${workOrderId}`
          // );
        }
      } else {
        // console.log(
        //   `Address already geocoded for work order ID: ${workOrderId}`
        // );
      }
    }

    if (hasNewAddresses) {
      //console.log("Updating geocoded addresses with new data");
      geocodedAddressesRef.current = newGeocodedAddresses;
      setGeocodedAddresses(newGeocodedAddresses);
    } else {
      //console.log("No new addresses to geocode");
    }
  }, [isLoaded, selectedWorkOrders, workOrders, addresses]);

  // Update ref when state changes
  useEffect(() => {
    geocodedAddressesRef.current = geocodedAddresses;
  }, [geocodedAddresses]);

  useEffect(() => {
    geocodeSelectedAddresses();
  }, [geocodeSelectedAddresses]);

  // Clean up selectedWorkOrdersOrder to match selectedWorkOrders
  useEffect(() => {
    setSelectedWorkOrdersOrder((prev) =>
      prev.filter((id) => selectedWorkOrders.includes(id)),
    );
  }, [selectedWorkOrders]);

  const calculateRoute = useCallback(async () => {
    if (selectedWorkOrdersOrder.length < 1 || !isLoaded) {
      setDirectionsResponse(null);
      return;
    }

    // Use the order array to get work orders in selection order
    // Use selectedWorkOrdersData instead of workOrders so data persists across searches
    const orderedWorkOrderData = selectedWorkOrdersOrder
      .map((id) => selectedWorkOrdersData.get(id))
      .filter(Boolean);

    if (orderedWorkOrderData.length < 1) {
      setDirectionsResponse(null);
      return;
    }

    // Filter out work orders that don't have geocoded addresses
    const validWorkOrders = orderedWorkOrderData.filter((wo) => {
      const address =
        addresses.get(wo!.invoiceDocumentIdentity) || wo!.customerAddress;
      return address && geocodedAddresses.has(wo!.invoiceDocumentIdentity);
    });

    if (validWorkOrders.length < 1) {
      setDirectionsResponse(null);
      return;
    }

    const directionsService = new google.maps.DirectionsService();

    // Build the route: startingPoint -> first invoice -> second invoice -> etc.
    let origin: string;
    let destination: string;
    let waypoints: google.maps.DirectionsWaypoint[] = [];

    if (startingPoint && startingPoint.trim()) {
      // Round trip: startingPoint -> all invoices -> back to startingPoint
      origin = startingPoint;
      destination = startingPoint;
      waypoints = validWorkOrders.map((wo) => ({
        location:
          addresses.get(wo!.invoiceDocumentIdentity) ||
          cleanAddress(wo!.customerAddress),
        stopover: true,
      }));
    } else {
      // No starting point, use first work order as origin
      origin =
        addresses.get(validWorkOrders[0]!.invoiceDocumentIdentity) ||
        cleanAddress(validWorkOrders[0]!.customerAddress);

      if (validWorkOrders.length === 1) {
        // Only one invoice and no starting point, don't calculate route
        setDirectionsResponse(null);
        return;
      } else {
        // Round trip: first invoice -> remaining invoices -> back to first invoice
        destination = origin;
        waypoints = validWorkOrders.slice(1).map((wo) => ({
          location:
            addresses.get(wo!.invoiceDocumentIdentity) ||
            cleanAddress(wo!.customerAddress),
          stopover: true,
        }));
      }
    }

    try {
      const result = await directionsService.route({
        origin: origin,
        destination: destination,
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false, // Don't optimize to maintain selection order
        avoidHighways: false,
        avoidTolls: false,
      });

      setDirectionsResponse(result);
    } catch (error: any) {
      console.error("Error calculating route:", error);
      setDirectionsResponse(null);

      // Check if it's a ZERO_RESULTS error (route cannot be calculated)
      const isZeroResults =
        error?.code === "ZERO_RESULTS" ||
        error?.message?.includes("ZERO_RESULTS") ||
        error?.message?.includes("No route could be found");

      if (isZeroResults) {
        // console.log(
        //   "[Route Calculation] ZERO_RESULTS - Route cannot be calculated, identifying problematic addresses",
        // );

        // Test each address individually to find which one(s) are problematic
        const testAddressForRouting = async (
          address: string,
          testFromAddress: string,
        ): Promise<boolean> => {
          try {
            const testService = new google.maps.DirectionsService();
            await testService.route({
              origin: testFromAddress,
              destination: address,
              travelMode: google.maps.TravelMode.DRIVING,
            });
            return true; // Route can be calculated
          } catch (err: any) {
            const isZeroResultsError =
              err?.code === "ZERO_RESULTS" ||
              err?.message?.includes("ZERO_RESULTS") ||
              err?.message?.includes("No route could be found");
            return !isZeroResultsError; // Return false if it's a ZERO_RESULTS error
          }
        };

        // Find problematic addresses by testing each one
        const problematicWorkOrderIds: number[] = [];

        // Determine test origin
        // If we have a starting point, test against it
        // If no starting point, test addresses against each other to find the problematic one
        let testOrigin: string | null = null;
        if (startingPoint && startingPoint.trim()) {
          testOrigin = startingPoint;
        } else if (validWorkOrders.length > 1) {
          // Use first work order as test origin (if it's not the problematic one)
          const firstWoAddress =
            addresses.get(validWorkOrders[0]!.invoiceDocumentIdentity) ||
            cleanAddress(validWorkOrders[0]!.customerAddress);
          if (firstWoAddress) {
            testOrigin = firstWoAddress;
          }
        }

        // If we have a single work order, it's definitely the problematic one
        if (validWorkOrders.length === 1) {
          problematicWorkOrderIds.push(
            validWorkOrders[0]!.invoiceDocumentIdentity,
          );
        } else if (testOrigin) {
          // Test each work order address individually against the test origin
          for (const wo of validWorkOrders) {
            const woAddress =
              addresses.get(wo!.invoiceDocumentIdentity) ||
              cleanAddress(wo!.customerAddress);

            if (!woAddress) {
              // No address, mark as problematic
              problematicWorkOrderIds.push(wo!.invoiceDocumentIdentity);
              continue;
            }

            // Skip testing if this is the test origin itself
            if (woAddress === testOrigin) {
              continue;
            }

            const canRoute = await testAddressForRouting(woAddress, testOrigin);
            if (!canRoute) {
              // console.log(
              //   `[Route Calculation] Address failed routing test from ${testOrigin} to ${woAddress} (Work Order: ${
              //     wo!.invoiceDocumentIdentity
              //   })`,
              // );
              problematicWorkOrderIds.push(wo!.invoiceDocumentIdentity);
            }
          }

          // Also test work orders sequentially to catch issues between work orders
          // (in case the issue is not with routing from starting point, but between work orders)
          if (
            problematicWorkOrderIds.length === 0 &&
            validWorkOrders.length > 1
          ) {
            // console.log(
            //   "[Route Calculation] No issues found from starting point, testing work orders sequentially",
            // );
            for (let i = 0; i < validWorkOrders.length - 1; i++) {
              const fromAddress =
                addresses.get(validWorkOrders[i]!.invoiceDocumentIdentity) ||
                cleanAddress(validWorkOrders[i]!.customerAddress);
              const toAddress =
                addresses.get(
                  validWorkOrders[i + 1]!.invoiceDocumentIdentity,
                ) || cleanAddress(validWorkOrders[i + 1]!.customerAddress);

              if (!fromAddress || !toAddress) {
                if (!fromAddress)
                  problematicWorkOrderIds.push(
                    validWorkOrders[i]!.invoiceDocumentIdentity,
                  );
                if (!toAddress)
                  problematicWorkOrderIds.push(
                    validWorkOrders[i + 1]!.invoiceDocumentIdentity,
                  );
                continue;
              }

              const canRoute = await testAddressForRouting(
                toAddress,
                fromAddress,
              );
              if (!canRoute) {
                // The destination address is problematic
                // console.log(
                //   `[Route Calculation] Address failed routing test from ${fromAddress} to ${toAddress} (Work Order: ${
                //     validWorkOrders[i + 1]!.invoiceDocumentIdentity
                //   })`,
                // );
                problematicWorkOrderIds.push(
                  validWorkOrders[i + 1]!.invoiceDocumentIdentity,
                );
                break; // Found the problematic one, stop testing
              }
            }
          }
        } else {
          // Can't determine test origin, test addresses in sequence to find where it breaks
          // console.log(
          //   "[Route Calculation] Testing addresses sequentially to find problematic one",
          // );
          for (let i = 0; i < validWorkOrders.length - 1; i++) {
            const fromAddress =
              addresses.get(validWorkOrders[i]!.invoiceDocumentIdentity) ||
              cleanAddress(validWorkOrders[i]!.customerAddress);
            const toAddress =
              addresses.get(validWorkOrders[i + 1]!.invoiceDocumentIdentity) ||
              cleanAddress(validWorkOrders[i + 1]!.customerAddress);

            if (!fromAddress || !toAddress) {
              if (!fromAddress)
                problematicWorkOrderIds.push(
                  validWorkOrders[i]!.invoiceDocumentIdentity,
                );
              if (!toAddress)
                problematicWorkOrderIds.push(
                  validWorkOrders[i + 1]!.invoiceDocumentIdentity,
                );
              continue;
            }

            const canRoute = await testAddressForRouting(
              toAddress,
              fromAddress,
            );
            if (!canRoute) {
              // The destination address is problematic
              // console.log(
              //   `[Route Calculation] Address failed routing test: ${toAddress} (Work Order: ${
              //     validWorkOrders[i + 1]!.invoiceDocumentIdentity
              //   })`,
              // );
              problematicWorkOrderIds.push(
                validWorkOrders[i + 1]!.invoiceDocumentIdentity,
              );
              break; // Found the problematic one, stop testing
            }
          }
        }

        // If we found problematic addresses, mark and uncheck only those
        if (problematicWorkOrderIds.length > 0) {
          // console.log(
          //   "[Route Calculation] Found problematic work orders:",
          //   problematicWorkOrderIds,
          // );

          // Mark as invalid and uncheck only the problematic ones
          setInvalidWorkOrderAddressIds((prev) => {
            const next = new Set(prev);
            problematicWorkOrderIds.forEach((id) => next.add(id));
            return next;
          });

          // Uncheck only problematic work orders
          setSelectedWorkOrders((prev) =>
            prev.filter((id) => !problematicWorkOrderIds.includes(id)),
          );
          setSelectedWorkOrdersOrder((prev) =>
            prev.filter((id) => !problematicWorkOrderIds.includes(id)),
          );

          // Clear their geocoded coordinates
          setGeocodedAddresses((prev) => {
            const next = new Map(prev);
            problematicWorkOrderIds.forEach((id) => {
              next.delete(id);
              geocodedAddressesRef.current.delete(id);
            });
            return next;
          });

          // console.log(
          //   "[Route Calculation] Unchecked only problematic work orders:",
          //   problematicWorkOrderIds,
          // );
        } else {
          // If we couldn't identify specific problematic addresses,
          // it might be the starting point or route combination issue
          // console.log(
          //   "[Route Calculation] Could not identify specific problematic addresses - route combination may be the issue",
          // );
        }
      }
    }
  }, [
    selectedWorkOrdersOrder,
    selectedWorkOrdersData,
    isLoaded,
    addresses,
    geocodedAddresses,
    startingPoint,
  ]);

  // Helper function to calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ): number => {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Calculate optimized route using multi-stop nearest-neighbor optimization
  // This finds the most efficient route without requiring a round trip
  const calculateOptimizedRoute = useCallback(async () => {
    if (selectedWorkOrdersOrder.length < 2 || !isLoaded) {
      setOptimizedDirectionsResponse(null);
      return;
    }

    // Use the order array to get work orders in selection order
    const orderedWorkOrderData = selectedWorkOrdersOrder
      .map((id) => selectedWorkOrdersData.get(id))
      .filter(Boolean);

    if (orderedWorkOrderData.length < 2) {
      setOptimizedDirectionsResponse(null);
      return;
    }

    // Filter out work orders that don't have geocoded addresses
    const validWorkOrders = orderedWorkOrderData.filter((wo) => {
      const address =
        addresses.get(wo!.invoiceDocumentIdentity) || wo!.customerAddress;
      return address && geocodedAddresses.has(wo!.invoiceDocumentIdentity);
    });

    if (validWorkOrders.length < 2) {
      setOptimizedDirectionsResponse(null);
      return;
    }

    const directionsService = new google.maps.DirectionsService();

    // Multi-stop optimization using nearest-neighbor algorithm
    // This creates an efficient route without returning to start
    if (startingPoint && startingPoint.trim() && startingPointCoordinates) {
      // Use nearest-neighbor algorithm to find optimal order
      const optimizedOrder: typeof validWorkOrders = [];
      const unvisited = [...validWorkOrders];
      let currentPosition = startingPointCoordinates;

      // Greedily select the nearest unvisited stop
      while (unvisited.length > 0) {
        let nearestIndex = 0;
        let nearestDistance = Infinity;

        for (let i = 0; i < unvisited.length; i++) {
          const wo = unvisited[i];
          const coords = geocodedAddresses.get(wo!.invoiceDocumentIdentity);
          if (coords) {
            const distance = calculateDistance(
              currentPosition.lat,
              currentPosition.lng,
              coords.lat,
              coords.lng,
            );
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = i;
            }
          }
        }

        // Move to nearest stop
        const nearest = unvisited.splice(nearestIndex, 1)[0];
        optimizedOrder.push(nearest);
        const nearestCoords = geocodedAddresses.get(
          nearest!.invoiceDocumentIdentity,
        );
        if (nearestCoords) {
          currentPosition = nearestCoords;
        }
      }

      // Round trip: startingPoint -> optimized stops -> back to startingPoint
      const origin = startingPoint;
      const destination = startingPoint;

      const waypoints = optimizedOrder.map((wo) => ({
        location:
          addresses.get(wo!.invoiceDocumentIdentity) ||
          cleanAddress(wo!.customerAddress),
        stopover: true,
      }));

      try {
        const result = await directionsService.route({
          origin: origin,
          destination: destination,
          waypoints,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false, // Don't re-optimize, we already computed optimal order
          avoidHighways: false,
          avoidTolls: false,
        });

        // Store the optimized order separately (work order IDs in optimized sequence)
        const optimizedIds = optimizedOrder.map(
          (wo) => wo!.invoiceDocumentIdentity,
        );
        setOptimizedRouteOrder(optimizedIds);
        setOptimizedDirectionsResponse(result);
      } catch (error: any) {
        console.error("Error calculating optimized route:", error);
        setOptimizedDirectionsResponse(null);
        setOptimizedRouteOrder([]);
      }
    } else {
      // Without starting point: use first work order as origin
      // Apply nearest-neighbor from the first stop
      const firstWo = validWorkOrders[0];
      const firstCoords = geocodedAddresses.get(
        firstWo!.invoiceDocumentIdentity,
      );

      if (!firstCoords) {
        setOptimizedDirectionsResponse(null);
        return;
      }

      const optimizedOrder: typeof validWorkOrders = [firstWo];
      const unvisited = validWorkOrders.slice(1);
      let currentPosition = firstCoords;

      while (unvisited.length > 0) {
        let nearestIndex = 0;
        let nearestDistance = Infinity;

        for (let i = 0; i < unvisited.length; i++) {
          const wo = unvisited[i];
          const coords = geocodedAddresses.get(wo!.invoiceDocumentIdentity);
          if (coords) {
            const distance = calculateDistance(
              currentPosition.lat,
              currentPosition.lng,
              coords.lat,
              coords.lng,
            );
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = i;
            }
          }
        }

        const nearest = unvisited.splice(nearestIndex, 1)[0];
        optimizedOrder.push(nearest);
        const nearestCoords = geocodedAddresses.get(
          nearest!.invoiceDocumentIdentity,
        );
        if (nearestCoords) {
          currentPosition = nearestCoords;
        }
      }

      // Round trip: first optimized stop -> remaining stops -> back to first
      const origin =
        addresses.get(optimizedOrder[0]!.invoiceDocumentIdentity) ||
        cleanAddress(optimizedOrder[0]!.customerAddress);
      const destination = origin;

      const waypoints = optimizedOrder.slice(1).map((wo) => ({
        location:
          addresses.get(wo!.invoiceDocumentIdentity) ||
          cleanAddress(wo!.customerAddress),
        stopover: true,
      }));

      try {
        const result = await directionsService.route({
          origin: origin,
          destination: destination,
          waypoints,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false,
          avoidHighways: false,
          avoidTolls: false,
        });

        // Store the optimized order separately (work order IDs in optimized sequence)
        const optimizedIds = optimizedOrder.map(
          (wo) => wo!.invoiceDocumentIdentity,
        );
        setOptimizedRouteOrder(optimizedIds);
        setOptimizedDirectionsResponse(result);
      } catch (error: any) {
        console.error("Error calculating optimized route:", error);
        setOptimizedDirectionsResponse(null);
        setOptimizedRouteOrder([]);
      }
    }
  }, [
    selectedWorkOrdersOrder,
    selectedWorkOrdersData,
    isLoaded,
    addresses,
    geocodedAddresses,
    startingPoint,
    startingPointCoordinates,
  ]);

  useEffect(() => {
    if (selectedWorkOrdersOrder.length >= 1) {
      calculateRoute();
    } else {
      setDirectionsResponse(null);
    }
  }, [selectedWorkOrdersOrder, calculateRoute]);

  // Calculate optimized route when user route changes
  useEffect(() => {
    if (selectedWorkOrdersOrder.length >= 2) {
      calculateOptimizedRoute();
    } else {
      setOptimizedDirectionsResponse(null);
      setOptimizedRouteOrder([]);
    }
  }, [selectedWorkOrdersOrder, calculateOptimizedRoute]);

  // Track when map is ready (has loaded and route is calculated)
  useEffect(() => {
    const isMapReady =
      isLoaded &&
      mapRef.current &&
      (selectedWorkOrdersOrder.length === 0 || directionsResponse !== null);
    setMapReady(isMapReady);
  }, [isLoaded, selectedWorkOrdersOrder.length, directionsResponse]);

  const handleMarkerClick = (
    workOrder: LogisticInvoice,
    position: { lat: number; lng: number },
  ) => {
    setSelectedPlace(position);
    setPlaceDetails({
      name: workOrder.customerName,
      formatted_address:
        addresses.get(workOrder.invoiceDocumentIdentity) ||
        workOrder.customerAddress,
      formatted_phone_number: workOrder.customerPhone,
      website: workOrder.customerEmail,
    });
  };

  // Update only the address text without triggering geocoding
  const updateAddressText = (id: number, address: string) => {
    setAddresses((prev) => {
      const next = new Map(prev);
      next.set(id, address);
      return next;
    });
  };

  // Update address and clear geocoded coordinates (for when user selects from suggestions)
  const updateAddress = (id: number, address: string) => {
    setAddresses((prev) => {
      const next = new Map(prev);
      next.set(id, address);
      return next;
    });
    setGeocodedAddresses((prev) => {
      const next = new Map(prev);
      next.delete(id);
      geocodedAddressesRef.current.delete(id);
      return next;
    });
  };

  const handleAddressSelect = (
    id: number,
    address: string,
    coordinates?: { lat: number; lng: number },
  ) => {
    // Update the address
    updateAddress(id, address);

    // If coordinates are provided, update geocoded addresses directly
    if (coordinates) {
      setInvalidWorkOrderAddressIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setGeocodedAddresses((prev) => {
        const next = new Map(prev);
        next.set(id, coordinates);
        geocodedAddressesRef.current.set(id, coordinates);
        return next;
      });
    } else {
      // If no coordinates provided, trigger geocoding for this specific address
      geocodeSpecificAddress(id, address);
      // Mark as invalid selection
      setInvalidWorkOrderAddressIds((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }
  };

  // Function to geocode a specific address
  const geocodeSpecificAddress = useCallback(
    async (id: number, address: string) => {
      if (!isLoaded || !address.trim()) return;

      try {
        const geocoder = new google.maps.Geocoder();
        const result = await new Promise<google.maps.GeocoderResult[]>(
          (resolve, reject) => {
            geocoder.geocode({ address: address.trim() }, (results, status) => {
              if (status === "OK" && results) {
                resolve(results);
              } else {
                console.warn(`Geocoding failed for ${address}:`, status);
                resolve([]);
              }
            });
          },
        );

        if (result.length > 0) {
          const location = result[0].geometry.location;
          const coordinates = {
            lat: location.lat(),
            lng: location.lng(),
          };

          setGeocodedAddresses((prev) => {
            const next = new Map(prev);
            next.set(id, coordinates);
            geocodedAddressesRef.current.set(id, coordinates);
            return next;
          });

          // Clear invalid status if address was successfully geocoded
          setInvalidWorkOrderAddressIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
          });

          //console.log(`Geocoded ${address} to:`, coordinates);
        } else {
          // Mark as invalid if geocoding fails
          setInvalidWorkOrderAddressIds((prev) => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
          });
        }
      } catch (error) {
        //console.error(`Error geocoding ${address}:`, error);
        // Mark as invalid on error
        setInvalidWorkOrderAddressIds((prev) => {
          if (prev.has(id)) return prev;
          const next = new Set(prev);
          next.add(id);
          return next;
        });
      }
    },
    [isLoaded],
  );

  const handleStartingPointSelect = (
    address: string,
    coordinates?: { lat: number; lng: number },
  ) => {
    // Only enable checkbox if user explicitly selects from autocomplete with valid coordinates
    if (coordinates) {
      setStartingPointCoordinates(coordinates);
      setMapCenter(coordinates);
      setHasUserProvidedStartingPoint(true);
    } else {
      // No coordinates means invalid selection - hide checkbox
      setStartingPointCoordinates(null);
      setHasUserProvidedStartingPoint(false);
    }
    setIsUserTypingStartingPoint(false);
  };

  // Handle when user starts typing in starting point field
  const handleStartingPointTyping = useCallback(() => {
    // Clear all selected work orders when user starts typing in starting point
    setSelectedWorkOrders([]);
    setSelectedWorkOrdersOrder([]);
    setSelectedWorkOrdersData(new Map());
    // Clear coordinates and hide checkbox when user starts typing (invalidates previous selection)
    setStartingPointCoordinates(null);
    setHasUserProvidedStartingPoint(false);
    setIsUserTypingStartingPoint(true);
    //console.log("Cleared selected work orders due to starting point typing");
  }, []);

  // Handler for toggling optimized route display with Amplitude logging
  const handleToggleOptimizedRoute = useCallback(() => {
    const newValue = !showOptimizedRoute;
    setShowOptimizedRoute(newValue);

    if (user) {
      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        "googleMapToggleOptimizedRoute",
        {
          elapsed_ms: "0",
          title: newValue ? "Show Optimized Route" : "Hide Optimized Route",
          componentName: "LogisticsContainer.tsx",
          version: packageJson.version,
        },
      );
    }
  }, [showOptimizedRoute, user]);

  // Monitor startingPoint - hide checkbox and clear coordinates if it becomes empty/null
  useEffect(() => {
    // console.log(
    //   "[Monitor startingPoint] Effect triggered, startingPoint:",
    //   startingPoint,
    // );
    if (!startingPoint || !startingPoint.trim()) {
      // console.log(
      //   "[Monitor startingPoint] StartingPoint is empty, clearing coordinates",
      // );
      setStartingPointCoordinates(null);
      setHasUserProvidedStartingPoint(false);
    }
  }, [startingPoint]);

  // Open print dialog if optimized route is available, otherwise print directly
  const openPrintDialog = () => {
    if (optimizedDirectionsResponse && selectedWorkOrders.length >= 2) {
      setShowPrintDialog(true);
    } else {
      // No optimized route available, print user route directly
      handlePrint("user");
    }
  };

  const handlePrintDialogConfirm = () => {
    setShowPrintDialog(false);
    handlePrint(printRouteType);
  };

  const handlePrint = async (routeType: "user" | "optimized" = "user") => {
    if (selectedWorkOrders.length === 0) return;

    setPrinting(true);

    // Wait for map to be ready (loaded and route calculated)
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds max wait
    while (!mapReady && attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    if (!mapReady) {
      console.warn("Map not ready after waiting, proceeding anyway");
    }

    // Capture current state at the time of printing to avoid stale closures
    const currentSelectedWorkOrders = [...selectedWorkOrders];
    const currentSelectedWorkOrdersOrder = [...selectedWorkOrdersOrder];
    const currentGeocodedAddresses = new Map(geocodedAddresses);
    const currentAddresses = new Map(addresses);
    // Use optimized or user route based on selection
    const currentDirectionsResponse =
      routeType === "optimized" && optimizedDirectionsResponse
        ? optimizedDirectionsResponse
        : directionsResponse;
    const currentStartingPoint = startingPoint;
    const currentStartingPointCoordinates = startingPointCoordinates;
    const isOptimizedRoute =
      routeType === "optimized" && optimizedDirectionsResponse;

    // console.log("Print function called with current state:", {
    //   selectedWorkOrders: currentSelectedWorkOrders,
    //   selectedWorkOrdersOrder: currentSelectedWorkOrdersOrder,
    //   startingPoint: currentStartingPoint,
    //   geocodedAddressesSize: currentGeocodedAddresses.size,
    //   addressesSize: currentAddresses.size,
    //   hasDirectionsResponse: !!currentDirectionsResponse,
    // });

    // Additional validation: ensure order array only contains selected items
    let validatedSelectedWorkOrdersOrder =
      currentSelectedWorkOrdersOrder.filter((id) =>
        currentSelectedWorkOrders.includes(id),
      );

    // If printing optimized route, use the optimizedRouteOrder
    if (isOptimizedRoute && optimizedRouteOrder.length > 0) {
      validatedSelectedWorkOrdersOrder = [...optimizedRouteOrder];
    }

    // console.log("Validated order after filtering:", {
    //   original: currentSelectedWorkOrdersOrder,
    //   validated: validatedSelectedWorkOrdersOrder,
    //   selected: currentSelectedWorkOrders,
    // });

    try {
      // Skip fetching place details to avoid reviews/extra info in printout
      //const placeDetailsMap = new Map();

      // Generate dynamic map HTML for print page
      let mapHtml = "";

      try {
        // Get the current map state
        const map = mapRef.current;
        if (map) {
          const center = map.getCenter();
          const zoom = map.getZoom();

          // console.log("Current map state:", {
          //   center: { lat: center.lat(), lng: center.lng() },
          //   zoom: zoom,
          //   hasRoute: !!(
          //     directionsResponse &&
          //     directionsResponse.routes &&
          //     directionsResponse.routes.length > 0
          //   ),
          // });

          // Create the same dynamic map for print page
          mapHtml = `
          <div class="map-section">
            <h3>${
              selectedWorkOrders.length > 1 ? "Route Map" : "Location Map"
            }</h3>
            <div id="print-map" style="width: 100%; height: 400px; border: 1px solid #ddd; border-radius: 8px;"></div>
          </div>
        `;
        }
      } catch (error) {
        //console.log("Error generating map for print:", error);
      }

      // Generate work order details with place information - use the validated order
      const workOrderDetails = validatedSelectedWorkOrdersOrder
        .map((workOrderId) => {
          const workOrder = workOrders.find(
            (wo) => wo.invoiceDocumentIdentity === workOrderId,
          );
          if (!workOrder) return null;

          const placeInfo = "";

          return `
        <div class="work-order-item">
          <div class="work-order-header">
            ${workOrder.documentNumber} - ${workOrder.customerName}
          </div>
          <div class="work-order-info">
            <p><strong>Address:</strong> ${
              currentAddresses.get(workOrder.invoiceDocumentIdentity) ||
              workOrder.customerAddress
            }</p>
            <p><strong>Phone:</strong> ${workOrder.customerPhone || ""}</p>
            <p><strong>Email:</strong> ${workOrder.customerEmail || ""}</p>
            ${
              workOrder.createdDate
                ? `<p><strong>Created:</strong> ${new Date(
                    workOrder.createdDate,
                  ).toLocaleDateString()}</p>`
                : ""
            }
            ${
              workOrder.status
                ? `<p><strong>status:</strong> ${workOrder.status}</p>`
                : ""
            }
            ${
              workOrder.technicianName
                ? `<p><strong>Technician:</strong> ${workOrder.technicianName}</p>`
                : ""
            }
            ${
              workOrder.workOrderDescription
                ? `<p><strong>Description:</strong> ${workOrder.workOrderDescription}</p>`
                : ""
            }
            ${
              workOrder.units && workOrder.units.length > 0
                ? `<div class="units-section">
                    <p><strong>Units:</strong></p>
                    <ul>
                      ${workOrder.units
                        .map(
                          (unit) => `
                        <li>
                          ${
                            unit.stockNo
                              ? `<div><strong>Stock No:</strong> ${unit.stockNo}</div>`
                              : ""
                          }
                          ${
                            unit.description
                              ? `<div><strong>Description:</strong> ${unit.description}</div>`
                              : ""
                          }
                          ${
                            unit.model
                              ? `<div><strong>Model:</strong> ${unit.model}</div>`
                              : ""
                          }
                          ${
                            unit.baseSerial
                              ? `<div><strong>Base Serial:</strong> ${unit.baseSerial}</div>`
                              : ""
                          }
                        </li>
                      `,
                        )
                        .join("")}
                    </ul>
                  </div>`
                : ""
            }
          </div>
          ${placeInfo}
        </div>
      `;
        })
        .filter(Boolean)
        .join("");

      // Prepare data for the print map - use the validated order
      const selectedWorkOrdersData = validatedSelectedWorkOrdersOrder
        .map((workOrderId) => {
          const workOrder = workOrders.find(
            (wo) => wo.invoiceDocumentIdentity === workOrderId,
          );
          if (!workOrder) return null;
          return {
            id: workOrder.invoiceDocumentIdentity,
            documentNumber: workOrder.documentNumber,
            customerName: workOrder.customerName,
            customerAddress:
              currentAddresses.get(workOrder.invoiceDocumentIdentity) ||
              workOrder.customerAddress,
            coordinates: currentGeocodedAddresses.get(
              workOrder.invoiceDocumentIdentity,
            ),
          };
        })
        .filter((wo) => wo && wo.coordinates);

      const mapData = {
        center: mapRef.current
          ? {
              lat: mapRef.current.getCenter().lat(),
              lng: mapRef.current.getCenter().lng(),
            }
          : mapCenter,
        zoom: mapRef.current ? mapRef.current.getZoom() : 10,
        selectedWorkOrders: currentSelectedWorkOrders,
        workOrders: selectedWorkOrdersData,
        directionsResponse: currentDirectionsResponse,
        startingPoint: currentStartingPoint,
        startingPointCoordinates: currentStartingPointCoordinates,
        validatedOrder: validatedSelectedWorkOrdersOrder, // helpful to have order explicitly
      };
      //debugger;
      // console.log("Print map data:", {
      //   selectedWorkOrders: currentSelectedWorkOrders,
      //   selectedWorkOrdersOrder: validatedSelectedWorkOrdersOrder,
      //   workOrdersData: selectedWorkOrdersData,
      //   startingPoint: currentStartingPoint,
      //   hasDirectionsResponse: !!currentDirectionsResponse,
      // });

      // Resolve favicon of the host app to use in the print window
      const existingFavicon = ((): string => {
        try {
          const link = document.querySelector(
            'link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]',
          ) as HTMLLinkElement | null;
          return link?.href || "/aspen.png";
        } catch {
          return "/aspen.png";
        }
      })();

      // Create print content in a non-blocking way
      const printContent = `
      <html>
        <head>
          <title>Logistics Map & Invoices Details</title>
          <link rel="icon" href="${existingFavicon}" />
          <link rel="shortcut icon" href="${existingFavicon}" />
          <link rel="apple-touch-icon" href="${existingFavicon}" />
          <style>
            body, html { margin: 0; padding: 20px; font-family: Arial, sans-serif; line-height: 1.6; }
            .print-container { width: 100%; max-width: 1200px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .header h1 { color: #333; margin: 0; }
            .header p { color: #666; margin: 5px 0; }
            .map-section { margin-bottom: 30px; text-align: center; }
            .map-section h3 { margin-bottom: 15px; color: #333; }
            .WorkOrderDetails { margin-top: 20px; }
            .work-order-item { margin-bottom: 25px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; background: #fff; }
            .work-order-header { font-weight: bold; color: #333; margin-bottom: 15px; font-size: 18px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
            .work-order-info { color: #666; font-size: 14px; }
            .work-order-info p { margin: 8px 0; }
            .place-details { margin-top: 20px; padding: 15px; background: #f9f9f9; border-radius: 5px; }
            .place-details h4 { margin-top: 0; color: #333; }
            .place-details ul { margin: 10px 0; padding-left: 20px; }
            .place-details li { margin-bottom: 3px; }
            .units-section { margin-top: 15px; padding: 10px; background: #f0f8ff; border-radius: 5px; border-left: 3px solid #2371E7; }
            .units-section p { margin: 0 0 10px 0; font-weight: bold; color: #333; }
            .units-section ul { margin: 0; padding-left: 20px; }
            .units-section li { margin-bottom: 8px; padding: 10px; background: #fff; border-radius: 3px; font-size: 13px; }
            .units-section li div { margin-bottom: 3px; }
            .units-section li div:last-child { margin-bottom: 0; }
            .reviews { margin-top: 10px; }
            .review { margin-bottom: 15px; padding: 10px; background: #fff; border-radius: 5px; border-left: 3px solid #2371E7; }
            .review p { margin: 5px 0; }
            .map-container { width: 100%; height: 400px; margin-bottom: 20px; border: 1px solid #ddd; }
            @media print {
              body { margin: 0; padding: 15px; }
              .work-order-item { page-break-inside: avoid; }
            }
          </style>
          <script src="https://maps.googleapis.com/maps/api/js?key=AIzaSyDipPUXM5YzthCmrSesDjYPvc6-LmfBByk"></script>
        </head>
        <body>
          <div class="print-container">
            <div class="header">
              <h1>Logistics Map & Invoice Details</h1>
              <p>Generated on: ${new Date().toLocaleString()}</p>
              <p>Total Invoices: ${currentSelectedWorkOrders.length}</p>
              <p style="color: ${isOptimizedRoute ? "#FF9800" : "#1976d2"}; font-weight: bold;">
                Route Type: ${isOptimizedRoute ? "Google Optimized Route" : "User Selected Route"}
              </p>
            </div>
            ${mapHtml}
            <div class="WorkOrderDetails">
              <h2>Selected Invoice Details</h2>
              ${workOrderDetails}
            </div>
          </div>
          
          <script>
            // Initialize the same map as the main page
            const mapData = ${JSON.stringify(mapData)};
            
            function initPrintMap() {
              const map = new google.maps.Map(document.getElementById('print-map'), {
                center: mapData.center,
                zoom: mapData.zoom,
                mapTypeId: google.maps.MapTypeId.ROADMAP
              });
              
              // Add starting point marker if exists
              if (mapData.startingPoint && mapData.startingPoint.trim() && mapData.workOrders.length > 0) {
                const startingPosition = mapData.startingPointCoordinates || mapData.center;
                new google.maps.Marker({
                  position: startingPosition,
                  map: map,
                  title: 'Starting Point: ' + mapData.startingPoint,
                  label: {
                    text: '1',
                    color: 'white',
                    fontWeight: 'bold'
                  },
                  icon: {
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                      '<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">' +
                        '<circle cx="20" cy="20" r="18" fill="#4CAF50" stroke="#fff" stroke-width="2"/>' +
                        '<text x="20" y="26" text-anchor="middle" fill="white" font-size="14" font-weight="bold">1</text>' +
                      '</svg>'
                    ),
                    scaledSize: new google.maps.Size(40, 40),
                    anchor: new google.maps.Point(20, 20)
                  }
                });
              }

              // Add markers for each work order with proper numbering
              mapData.workOrders.forEach((workOrder, index) => {
                if (workOrder.coordinates) {
                  // Calculate the correct stop number based on starting point
                  const stopNumber = mapData.startingPoint && mapData.startingPoint.trim() ? index + 2 : index + 1;
                  
                  new google.maps.Marker({
                    position: workOrder.coordinates,
                    map: map,
                    title: workOrder.documentNumber + ' - ' + workOrder.customerName,
                    label: {
                      text: String(stopNumber), // Show correct stop number
                      color: 'white',
                      fontWeight: 'bold'
                    }
                  });
                }
              });
              
              // Add directions if available
              if (mapData.directionsResponse && mapData.directionsResponse.routes && mapData.directionsResponse.routes.length > 0) {
                const directionsRenderer = new google.maps.DirectionsRenderer({
                  map: map,
                  suppressMarkers: true // We already have our own markers
                });
                directionsRenderer.setDirections(mapData.directionsResponse);
              } else if (mapData.workOrders.length > 1) {
                // If no directions response but we have multiple work orders, calculate route
                const directionsService = new google.maps.DirectionsService();
                
                let origin, destination, waypoints = [];
                
                if (mapData.startingPoint && mapData.startingPoint.trim()) {
                  // Starting point is the first stop
                  origin = mapData.startingPoint;
                  
                  if (mapData.workOrders.length === 1) {
                    destination = mapData.workOrders[0].customerAddress;
                    waypoints = [];
                  } else {
                    destination = mapData.workOrders[mapData.workOrders.length - 1].customerAddress;
                    waypoints = mapData.workOrders.slice(0, -1).map(wo => ({
                      location: wo.customerAddress,
                      stopover: true
                    }));
                  }
                } else {
                  // No starting point
                  origin = mapData.workOrders[0].customerAddress;
                  destination = mapData.workOrders[mapData.workOrders.length - 1].customerAddress;
                  waypoints = mapData.workOrders.length > 2 
                    ? mapData.workOrders.slice(1, -1).map(wo => ({
                        location: wo.customerAddress,
                        stopover: true
                      }))
                    : [];
                }
                
                directionsService.route({
                  origin: origin,
                  destination: destination,
                  waypoints: waypoints,
                  travelMode: google.maps.TravelMode.DRIVING,
                  optimizeWaypoints: false
                }, (result, status) => {
                  if (status === 'OK') {
                    const directionsRenderer = new google.maps.DirectionsRenderer({
                      map: map,
                      suppressMarkers: true
                    });
                    directionsRenderer.setDirections(result);
                  }
                });
              }
            }
            
            // Initialize map when Google Maps API is loaded
            if (typeof google !== 'undefined' && google.maps) {
              initPrintMap();
            } else {
              google.maps.event.addDomListener(window, 'load', initPrintMap);
            }
          </script>
        </body>
      </html>
    `;

      // Use setTimeout to make this non-blocking
      setTimeout(() => {
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.focus();

          // Wait for map to load before printing
          setTimeout(() => {
            printWindow.print();
            // Don't close immediately, let user decide
            // printWindow.close();
          }, 2000); // Increased timeout to allow map to fully load
        }
      }, 0);
    } catch (error) {
      console.error("Error generating print content:", error);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Box
      p={2}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        position: "relative", // Required for absolute positioned backdrop
      }}
    >
      {/* Filters Row */}
      <LogisticsFilters
        dateRange={dateRange}
        searchText={searchText}
        setDateRange={setDateRange}
        setSearchText={setSearchText}
        onReset={resetFilters}
        WorkOrderStatuses={woStatuses}
        WorkOrderStatusIds={WorkOrderStatusIds}
        setWorkOrderStatusIds={setWorkOrderStatusIds}
        locations={locations}
        locationId={locationId}
        setLocationId={setLocationId}
        startingPoint={startingPoint}
        setStartingPoint={setStartingPoint}
        defaultStartingPoint={user?.locationAddress || ""}
        includeRentalContracts={includeRentalContracts}
        setIncludeRentalContracts={setIncludeRentalContracts}
        onStartingPointSelect={handleStartingPointSelect}
        onStartingPointTyping={handleStartingPointTyping}
        startingPointError={!!startingPoint && !startingPointCoordinates}
        startingPointErrorText={
          !!startingPoint && !startingPointCoordinates
            ? "Please search and select a valid address from suggestions"
            : undefined
        }
        pickupDateRange={pickupDateRange}
        setPickupDateRange={setPickupDateRange}
        deliveryDateRange={deliveryDateRange}
        setDeliveryDateRange={setDeliveryDateRange}
      />

      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            height: "100%",
            minHeight: 0,
            minWidth: { xs: "1000px", lg: "100%" },
          }}
        >
          {/* Work Orders List */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flex: isListCollapsed
                ? "0 0 37px"
                : { xs: "0 0 380px", md: "0 0 35%" },
              minWidth: isListCollapsed ? "30px" : "360px",
              overflow: "hidden",

              maxHeight: "100%",
              minHeight: 0,
              transition: "flex 0.3s ease, min-width 0.3s ease",
            }}
          >
            {/* Collapse/Expand toggle button */}
            <Box
              sx={{
                position: "absolute",
                right: 0,

                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
              }}
            >
              <Tooltip
                title={isListCollapsed ? "Expand Section" : "Collapse Section"}
              >
                <IconButton
                  onClick={() => setIsListCollapsed(!isListCollapsed)}
                  size="small"
                  sx={{
                    color: "#1976d2",
                    backgroundColor: "white",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "50%",
                    boxShadow: theme.shadows[2],
                    "&:hover": {
                      bgcolor: theme.palette.action.hover,
                    },
                  }}
                >
                  {isListCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            {/* List content */}
            <Box
              sx={{
                flex: 1,
                overflow: "hidden",
                opacity: isListCollapsed ? 0 : 1,
                visibility: isListCollapsed ? "hidden" : "visible",
                transition: "opacity 0.2s ease, visibility 0.2s ease",
              }}
            >
              <LogisticsList
                workOrders={workOrders}
                selectedWorkOrders={selectedWorkOrders}
                onToggle={handleCheckboxChange}
                onPrint={openPrintDialog}
                printing={printing}
                onUpdateAddressText={updateAddressText}
                onUpdateAddress={updateAddress}
                addresses={addresses}
                onAddressSelect={handleAddressSelect}
                showSelectionCheckbox={!!startingPointCoordinates}
                invalidAddressIds={invalidWorkOrderAddressIds}
                isDisabled={!startingPointCoordinates}
              />
            </Box>
          </Box>

          {/* Map */}
          <Box
            ref={printRef}
            sx={{
              flexGrow: 1,
              minHeight: 0,
              minWidth: { xs: "600px", md: 0 },
            }}
          >
            <LogisticsMapPanel
              isLoaded={isLoaded}
              loadError={loadError}
              mapRef={mapRef}
              containerStyle={containerStyle}
              center={mapCenter}
              workOrders={workOrders as any}
              selectedWorkOrders={selectedWorkOrders}
              selectedWorkOrdersOrder={selectedWorkOrdersOrder}
              selectedWorkOrdersData={selectedWorkOrdersData}
              geocodedAddresses={geocodedAddresses}
              directionsResponse={directionsResponse}
              optimizedDirectionsResponse={optimizedDirectionsResponse}
              optimizedRouteOrder={optimizedRouteOrder}
              showOptimizedRoute={showOptimizedRoute}
              onToggleOptimizedRoute={handleToggleOptimizedRoute}
              placeDetails={placeDetails}
              selectedPlace={selectedPlace}
              onMarkerClick={handleMarkerClick as any}
              onCloseInfo={() => setPlaceDetails(null)}
              directionsRendererRef={directionsRendererRef}
              startingPoint={startingPoint}
              startingPointCoordinates={startingPointCoordinates}
              addresses={addresses}
              onDirectionsChanged={(updated) => {
                try {
                  if (
                    !updated ||
                    !updated.routes ||
                    updated.routes.length === 0
                  )
                    return;
                  const legs = updated.routes[0].legs || [];
                  const sig = legs
                    .map((leg: any) =>
                      leg.end_location ? leg.end_location.toUrlValue() : "",
                    )
                    .join("|");
                  if (lastRouteSignatureRef.current === sig) return;
                  lastRouteSignatureRef.current = sig;
                  setDirectionsResponse(updated);
                } catch {
                  setDirectionsResponse(updated);
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      {loading && <GlobalSpinner />}

      {/* Map loading overlay */}
      {!mapReady && selectedWorkOrdersOrder.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <GlobalSpinner />
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            Preparing map and route...
          </Typography>
        </Box>
      )}

      {/* Print Route Selection Dialog */}
      <Dialog
        open={showPrintDialog}
        onClose={() => setShowPrintDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={(theme) => ({
            m: 0,
            backgroundColor:
              theme.palette.mode === "light"
                ? theme.palette.primary.main
                : "#181F2A",
            color:
              theme.palette.mode === "light"
                ? theme.palette.primary.contrastText
                : "#fff",
            p: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          Select Route to Print
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which route you would like to print:
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={printRouteType}
              onChange={(e) =>
                setPrintRouteType(e.target.value as "user" | "optimized")
              }
            >
              <FormControlLabel
                value="user"
                control={<Radio color="primary" />}
                label={
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "#1976d2" }}
                    >
                      Your Selected Route
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Print stops in the order you selected them
                    </Typography>
                  </Box>
                }
                sx={{
                  mb: 1,
                  alignItems: "flex-start",
                  "& .MuiRadio-root": { pt: 0.5 },
                }}
              />
              <FormControlLabel
                value="optimized"
                control={<Radio color="warning" />}
                label={
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "#FF9800" }}
                    >
                      Google Optimized Route
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Print stops in the most efficient order calculated by
                      Google
                    </Typography>
                  </Box>
                }
                sx={{
                  alignItems: "flex-start",
                  "& .MuiRadio-root": { pt: 0.5 },
                }}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            sx={{ textTransform: "capitalize" }}
            onClick={() => setShowPrintDialog(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePrintDialogConfirm}
            variant="contained"
            sx={{
              textTransform: "capitalize",
              bgcolor: printRouteType === "optimized" ? "#FF9800" : "#1976d2",
              "&:hover": {
                bgcolor: printRouteType === "optimized" ? "#F57C00" : "#1565C0",
              },
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogisticsContainer;
